import { useAlert } from '@/common/alert/AlertContext';
import { useDialog } from '@/common/dialog/DialogContext';
import { useEntityFormDialogActions } from '@/common/entityFramework/entityForm/EntityFormDialog';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { useForm } from '@/common/form/FormContext';
import { EntitySelect } from '@/common/form/formFields/EntitySelect';
import { useFormFieldDef } from '@/common/form/hooks/useFormFieldDef';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

// Client-only field (not persisted) holding every Product picked in ADD mode, so ProgPriceForm can
// create one Program Price per Product on save
export const PROD_SELECTIONS_FIELD = 'prodSelections';

// A Program Price normally references a single Product, but when adding a new one the user may pick
// several Products at once; this field lets them do so and defers to the plain single-select EntitySelect
// once the Program Price already exists (view/edit). allowMultiple overrides the page-level mode check when embedded.
// prodSetDefaults ({ code, name }) seeds the "combine into a Product Set" dialog offered when 2+ Products are picked.
export const ProgProdProduct = ({ fieldDef, allowMultiple, prodSetDefaults }) => {
  const { entityFormMode } = useEntityMgr();
  const { formValues, assignValue } = useForm();
  const { openDialog } = useDialog();
  const { showAlert } = useAlert();
  const rsrc = useRsrc();
  const { setExtraButtons } = useEntityFormDialogActions();
  const { onBlur, error, helperText, label, variant } = useFormFieldDef(fieldDef);

  const multiple = allowMultiple ?? entityFormMode === EntityFormMode.ADD;
  const prodSelections = formValues[PROD_SELECTIONS_FIELD] ?? [];

  const assignSelections = (selectedEntities) => {
    assignValue(PROD_SELECTIONS_FIELD, selectedEntities);
    assignValue('prodId', selectedEntities[0]?.id ?? null);
    assignValue('prodCode', selectedEntities[0]?.code ?? null);
    assignValue('prodName', selectedEntities[0]?.name ?? null);
  };

  // Replaces the picked Products with one Product Set containing exactly them (reusing a matching set if one exists)
  const combineIntoProdSet = async () => {
    const prodIds = prodSelections.map((prod) => prod.id);
    let res = await rsrc.store('md', 'PROD_SET_FROM_PRODS_STORE_PLUGIN', { prodIds });

    if (!res.prodSet) {
      const named = await openDialog(ProdSetNameDialog, {
        defaults: prodSetDefaults,
        prodCodes: prodSelections.map((prod) => prod.code),
      });
      if (!named) return;
      res = await rsrc.store('md', 'PROD_SET_FROM_PRODS_STORE_PLUGIN', { prodIds, ...named });
    }

    assignSelections([res.prodSet]);
    showAlert(
      res.existing ?
        `Using existing product set ${res.prodSet.code}, which already contains exactly these products`
      : `Created product set ${res.prodSet.code}`,
      res.existing ? 'info' : 'success',
    );
  };

  useEffect(() => {
    if (!multiple || prodSelections.length < 2) {
      setExtraButtons(null);
      return;
    }
    setExtraButtons(
      <Button
        color="primary"
        variant="outlined"
        startIcon={<FontAwesomeIcon icon={fas.faFolderTree} />}
        onClick={combineIntoProdSet}
      >
        Combine into product set
      </Button>,
    );
    return () => setExtraButtons(null);
  }, [multiple, prodSelections]);

  if (!multiple) {
    return <EntitySelect fieldDef={fieldDef} />;
  }

  const handleClick = () => {
    if (prodSelections.length > 0) {
      assignSelections([]);
      return;
    }

    openDialog(ProgPriceProductSelectDialog, {
      data: { module: fieldDef.module, entity: fieldDef.entity },
    }).then((selectedEntities) => {
      onBlur();
      if (!selectedEntities || selectedEntities.length === 0) return;
      assignSelections(selectedEntities);
    });
  };

  return (
    <TextField
      variant={variant}
      error={error}
      helperText={helperText}
      label={label}
      onBlur={onBlur}
      value={prodSelections.map((prod) => prod.code).join(', ')}
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleClick} edge="end">
                <Icon>
                  <FontAwesomeIcon
                    style={{ width: '100%' }}
                    icon={prodSelections.length > 0 ? fas.faTimes : fas.faSearch}
                  />
                </Icon>
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

// A Product picker that always allows selecting multiple rows, used only for the ADD-mode multi-Product flow above
const ProgPriceProductSelectDialog = ({ data, closeDialog }) => {
  const [selectedEntities, setSelectedEntities] = useState([]);

  const augFieldDefs = (fieldToFieldDef) => {
    Object.values(fieldToFieldDef).forEach((fieldDef) => {
      fieldDef.disableHyperlink = true;
    });

    fieldToFieldDef.checkbox = GridConstants.checkbox;

    GridConstants.removeAuditFieldDefs(fieldToFieldDef);
  };

  const rowSelected = useCallback((event) => {
    setSelectedEntities(event.api.getSelectedRows());
  }, []);

  const submit = () => {
    closeDialog(selectedEntities);
  };

  return (
    <Dialog onClose={() => closeDialog()} open={true} fullWidth maxWidth="lg">
      <DialogTitle>Select Products</DialogTitle>
      <DialogContent>
        <Box sx={{ height: '60vh' }}>
          <EntityList
            module={data.module}
            entity={data.entity}
            augFieldDefs={augFieldDefs}
            gridOptions={{ rowSelection: 'multiple' }}
            events={{ onRowSelected: rowSelected }}
            showTitle={false}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={submit} disabled={selectedEntities.length === 0}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Asks for the code/name of a new Product Set; resolves { code, name } or undefined
const ProdSetNameDialog = ({ defaults, prodCodes = [], closeDialog }) => {
  const [code, setCode] = useState(defaults?.code ?? '');
  const [name, setName] = useState(defaults?.name ?? '');
  const [startDate, setStartDate] = useState(defaults?.startDate ?? null);
  const [endDate, setEndDate] = useState(defaults?.endDate ?? null);
  const invalidDates = startDate == null || endDate == null || startDate > endDate;

  return (
    <Dialog onClose={() => closeDialog()} open={true} fullWidth maxWidth="sm">
      <DialogTitle>New Product Set</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            No existing product set contains exactly these {prodCodes.length} products ({prodCodes.join(', ')}). Name
            the new set:
          </Box>
          <TextField label="Code" value={code} onChange={(event) => setCode(event.target.value)} autoFocus required />
          <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              type="date"
              label="Member start date"
              value={toDateInput(startDate)}
              onChange={(event) => setStartDate(fromDateInput(event.target.value))}
              slotProps={{ inputLabel: { shrink: true } }}
              required
            />
            <TextField
              fullWidth
              type="date"
              label="Member end date"
              value={toDateInput(endDate)}
              onChange={(event) => setEndDate(fromDateInput(event.target.value))}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              error={startDate != null && endDate != null && startDate > endDate}
              helperText={startDate != null && endDate != null && startDate > endDate ? 'Before start date' : ''}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!code.trim() || invalidDates}
          onClick={() => closeDialog({ code: code.trim(), name: name.trim() || code.trim(), startDate, endDate })}
        >
          Create product set
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const toDateInput = (date) => {
  if (date == null) return '';
  const text = String(date);
  return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
};

const fromDateInput = (value) => (value ? DateUtils.parse(value, 'YYYY-MM-DD') : null);
